/**
 * Pure formatting for the "Ours · Tharstern · Δ" comparison — the single UX
 * rule the product owner set after reviewing the cost-engine validation
 * mock preview: every measured quantity on the print-technical validation
 * pages shows our engine's figure, Tharstern's recorded figure, and the
 * signed difference between them together, never one alone and never the
 * difference alone.
 *
 * `ours` and `tharstern` are the raw stored units (pence for money, minutes
 * for time, a plain count for quantity) — never pre-divided or pre-formatted
 * by the caller, matching this repo's pence-everywhere convention
 * (`formatQuotedPrice`'s header comment).
 */

import { formatCurrencyGbp } from "./formatters";

export type ComparisonKind = "money" | "time" | "quantity";

/** Matches `ValidationTone` in `cost-engine-validation-presentation.ts` —
 *  the cost-check-state colour tokens this comparison reuses for its Δ. */
export type ComparisonTone = "success" | "warning" | "error" | "neutral";

export interface ComparisonValue {
  kind: ComparisonKind;
  /** Pence for money, minutes for time, a raw count for quantity. */
  ours: number | null;
  /** `null` means Tharstern has no recorded value for this measurement —
   *  never checked, or not priceable from spec. Renders as "—"; pass
   *  `dashReason` to explain why in a tooltip. */
  tharstern: number | null;
  /** Quantity kind only — unit suffix, e.g. "sheets". */
  unit?: string;
  /** Explains a `tharstern: null` dash. Ignored otherwise. */
  dashReason?: string;
  /**
   * Use this tone instead of the one computed from the delta's magnitude.
   * Pass it whenever the caller already has an authoritative cost-check
   * state sitting next to this figure (a `result` badge on a check, a
   * matrix cell's `state`) — magnitude alone can disagree with that state
   * (e.g. a tiny delta on a "red" check because red means *systematic*, not
   * *large*), and showing two different colours for the same fact is
   * exactly the confusion this comparison exists to remove. Ignored when
   * there is nothing to compare (the dash case).
   */
  toneOverride?: ComparisonTone;
}

export interface ComparisonTriplet {
  oursText: string;
  tharsternText: string;
  deltaText: string;
  /** Signed, one decimal place (e.g. "-5.2%"). `null` when there is nothing
   *  to divide by, or the values are exact. */
  deltaPercentText: string | null;
  tone: ComparisonTone;
  isExact: boolean;
  hasTharstern: boolean;
}

/** A delta within this band of Tharstern's value is a "small difference"
 *  (amber) rather than a "mismatch" (red) — the same 5% line the dictionary
 *  index page already reports coverage against (`within5PctPct`). */
const SMALL_DIFFERENCE_PCT = 5;

function formatMinutes(minutes: number): string {
  const rounded = Math.round(minutes * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} min`;
}

function formatQuantity(value: number, unit?: string): string {
  const text = value.toLocaleString("en-GB");
  return unit ? `${text} ${unit}` : text;
}

function formatValue(
  kind: ComparisonKind,
  value: number,
  unit?: string,
): string {
  switch (kind) {
    case "money":
      return formatCurrencyGbp(value / 100, 2);
    case "time":
      return formatMinutes(value);
    case "quantity":
      return formatQuantity(value, unit);
  }
}

/** `Intl.NumberFormat` renders a negative sign as the ASCII hyphen-minus
 *  (U+002D), not the Unicode minus sign (U+2212) the glossary calls for
 *  ("−£0.60 (−5.2%)", not "-£0.60(-5.2%)") — typographically a hyphen is
 *  too short and sits high, reading as a stray dash rather than a minus
 *  next to a currency symbol. Swap only a *leading* hyphen (the sign),
 *  never one inside the value. */
function toProperMinus(text: string): string {
  return text.replace(/^-/, "−");
}

/** Signed delta in the same unit as the measurement — money already carries
 *  its own sign via `Intl`'s currency formatting; time/quantity need an
 *  explicit `+` for positive deltas ("-" is native to the number). */
function formatDelta(
  kind: ComparisonKind,
  delta: number,
  unit?: string,
): string {
  if (kind === "money") return toProperMinus(formatCurrencyGbp(delta / 100, 2));
  const sign = delta > 0 ? "+" : "";
  return toProperMinus(`${sign}${formatValue(kind, delta, unit)}`);
}

const PERCENT_FORMATTER = new Intl.NumberFormat("en-GB", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});

/**
 * Compute the Ours/Tharstern/Δ triplet for one measurement.
 *
 * Tone follows the cost-check-state ladder used everywhere else on these
 * pages: exact → green, a small difference (within
 * {@link SMALL_DIFFERENCE_PCT} of Tharstern's value) → amber, anything
 * larger → red. Percentage is
 * relative to Tharstern's value (how far off its baseline we are), not
 * ours — matching `within5PctPct`'s own denominator.
 */
export function computeComparisonTriplet(
  value: ComparisonValue,
): ComparisonTriplet {
  const { kind, ours, tharstern, unit, toneOverride } = value;

  if (ours == null || tharstern == null) {
    return {
      oursText: ours == null ? "—" : formatValue(kind, ours, unit),
      tharsternText:
        tharstern == null ? "—" : formatValue(kind, tharstern, unit),
      deltaText: "—",
      deltaPercentText: null,
      tone: "neutral",
      isExact: false,
      hasTharstern: tharstern != null,
    };
  }

  const delta = ours - tharstern;
  const isExact = delta === 0;
  const pct = tharstern !== 0 ? (delta / Math.abs(tharstern)) * 100 : null;
  const tone: ComparisonTone =
    toneOverride ??
    (isExact
      ? "success"
      : pct !== null && Math.abs(pct) <= SMALL_DIFFERENCE_PCT
        ? "warning"
        : "error");

  return {
    oursText: formatValue(kind, ours, unit),
    tharsternText: formatValue(kind, tharstern, unit),
    deltaText: isExact ? "exact" : formatDelta(kind, delta, unit),
    deltaPercentText:
      !isExact && pct !== null
        ? toProperMinus(PERCENT_FORMATTER.format(pct / 100))
        : null,
    tone,
    isExact,
    hasTharstern: true,
  };
}

/** The one-line explainer every page/section carrying this comparison shows
 *  once, near the top (product-owner rule: idiot-proof, no guessing). */
export const OURS_THARSTERN_LEGEND =
  "Ours = our engine's cost; Tharstern = its recorded cost for the same route; Δ = Ours − Tharstern (negative = we are under).";
